// ========================================
// Hook לניהול דריסות תחזית (Forecast Overrides)
// ========================================
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook לניהול דריסות תחזית מ-Supabase
 */
export const useForecastOverrides = () => {
    const [overrides, setOverrides] = useState({}); // Map: sku -> override object
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // שליפת כל הדריסות
    const fetchOverrides = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const today = new Date().toISOString().split('T')[0];

            // שליפה רק של דריסות שעדיין בתוקף (או ללא תאריך תפוגה)
            const { data, error: dbError } = await supabase
                .from('forecast_overrides')
                .select('*')
                .or(`expiry_date.is.null,expiry_date.gte.${today}`);

            if (dbError) throw dbError;

            // המרה ל-Map לגישה מהירה
            const overridesMap = {};
            data.forEach(item => {
                overridesMap[item.sku] = {
                    factor: item.manual_forecast_factor,
                    notes: item.notes,
                    expiryDate: item.expiry_date,
                    updatedAt: item.updated_at,
                };
            });

            setOverrides(overridesMap);

        } catch (err) {
            console.error('Error fetching overrides:', err);
            setError('שגיאה בטעינת דריסות התחזית');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // קבלת דריסה לפריט ספציפי
    const getOverride = useCallback((sku) => {
        return overrides[sku] || null;
    }, [overrides]);

    // בדיקה אם יש דריסה פעילה לפריט
    const hasOverride = useCallback((sku) => {
        return !!overrides[sku];
    }, [overrides]);

    // הגדרת/עדכון דריסה
    const setOverride = useCallback(async (sku, factor, notes = null, expiryDate = null) => {
        try {
            const { error: dbError } = await supabase
                .from('forecast_overrides')
                .upsert({
                    sku,
                    manual_forecast_factor: factor,
                    notes,
                    expiry_date: expiryDate,
                    updated_at: new Date().toISOString(),
                }, { onConflict: 'sku' });

            if (dbError) throw dbError;

            // עדכון מקומי
            setOverrides(prev => ({
                ...prev,
                [sku]: { factor, notes, expiryDate, updatedAt: new Date().toISOString() }
            }));

            return { success: true };

        } catch (err) {
            console.error('Error setting override:', err);
            return { success: false, error: err.message };
        }
    }, []);

    // מחיקת דריסה
    const deleteOverride = useCallback(async (sku) => {
        try {
            const { error: dbError } = await supabase
                .from('forecast_overrides')
                .delete()
                .eq('sku', sku);

            if (dbError) throw dbError;

            // עדכון מקומי
            setOverrides(prev => {
                const newOverrides = { ...prev };
                delete newOverrides[sku];
                return newOverrides;
            });

            return { success: true };

        } catch (err) {
            console.error('Error deleting override:', err);
            return { success: false, error: err.message };
        }
    }, []);

    // שליפה ראשונית
    useEffect(() => {
        fetchOverrides();
    }, [fetchOverrides]);

    return {
        overrides,
        isLoading,
        error,
        getOverride,
        hasOverride,
        setOverride,
        deleteOverride,
        refetch: fetchOverrides,
    };
};
